import { getEnv, NAME } from 'config';
import { each, find } from 'lodash';
import { defineStore } from 'pinia';
import i18n, { tr } from 'plugins/i18n';
import { store as i18nStore } from 'plugins/i18n/store';
import useRouter from 'plugins/router';
import appStore from 'plugins/store/app';
import { pinia } from 'plugins/store/index';
import { computed, ref } from 'vue';
interface ISEO extends Obj {
  metadata: any,
  translations?: any,
}

const useStore = defineStore('seo', () => {
  const state = ref<ISEO>({
    metadata: {},
    translations: undefined,
  });

  //Actions
  const UPDATE = (dataSeo?: SeoData, dataTranslations?: any) => {
    // @todo check why seo reset
    // if(!data) return
    const router = useRouter()
    const route = router.currentRoute.value.meta.id;
    const isRedirectedFrom = router.currentRoute.value.redirectedFrom;
    const fullUrl = `${getEnv('VITE_SITE_URL')}${router.currentRoute.value.fullPath}`;
    
    const result: any = {
      title: undefined,
      htmlAttrs: {},
      meta: [],
      link: [],
    };

    // set current lang html
    if (i18nStore.localeName) {
      result.htmlAttrs.lang = i18nStore.localeName;
    }

    // check default seo title from i18n files
    if (i18n.global.te(`SEO.${route}`, i18nStore.locale?.i18n) && i18n.global.te(`SEO.${route}.title`, i18nStore.locale?.i18n)) {
      result.title = i18n.global.t(`SEO.${route}.title`);
    } else if (i18n.global.t('SEO.default.title')) {
      result.title = i18n.global.t('SEO.default.title');
    }

    // check default seo meta from i18n files
    if (i18n.global.te(`SEO.${route}`, i18nStore.locale?.i18n) && i18n.global.te(`SEO.${route}.meta`, i18nStore.locale?.i18n)) {
      result.meta = tr(`SEO.${route}.meta`)
    } else if (i18n.global.te('SEO.default', i18nStore.locale?.i18n) && i18n.global.te('SEO.default.meta', i18nStore.locale?.i18n)) {
      result.meta = tr('SEO.default.meta')
    }

    // check default seo link from i18n files -> alternate links
    if (i18n.global.te(`SEO.${route}`, i18nStore.locale?.i18n) && i18n.global.te(`SEO.${route}.link`, i18nStore.locale?.i18n)) {
      result.link = tr(`SEO.${route}.link`)
    } else if (i18n.global.te('SEO.default', i18nStore.locale?.i18n) && i18n.global.te('SEO.default.link', i18nStore.locale?.i18n)) {
      result.link = tr('SEO.default.link')
    }
    
    // check if api return actual seo data..
    if (dataSeo) {
      const seo = computeSeoData(dataSeo)
      const socials: Obj = {};

      if (seo.title) {
        result.title = seo.title;
      }

      // we can push here because vueuse will add only the last occurence from each og tag with the same name if there is no :key
      if (seo.description) {
        result.meta.push({
          name: 'description',
          content: seo.description,
        });
      }

      socials.fb = {
        app_id: getEnv('VITE_FB_APP_ID') || '',
      };

      if (seo.socials?.facebook) {
        const facebook = seo.socials.facebook;
        socials.og = {
          title: facebook.title,
          description: facebook.description,
          image: facebook.image?.src,
          'image:alt': facebook.image?.alt,
          'image:width': facebook.image?.width,
          'image:height': facebook.image?.height,
          url: fullUrl,
          locale: i18nStore.locale?.name,
          type: 'website',
          site_name: NAME,
        };
      }

      if (seo.socials?.twitter) {
        const twitter = seo.socials.twitter;
        socials.twitter = {
          title: twitter.title,
          description: twitter.description,
          image: twitter.image?.src,
          'image:alt': twitter.image?.alt,
          card: 'summary_large_image',
          url: fullUrl,
        };
      }

      each(socials, (props, k) => {
        each(props, (content, name) => {
          if (content) {
            const key = `${k}:${name}`;
            result.meta.push({
              key: `${key}:${content}`,
              name: key,
              content,
            });
          }
        });
      });
    }

    // load althernate link from api data. if needed
    if (i18nStore.otherLocales && i18nStore.otherLocales.length >= 1) {
      // add current locale
      if (i18nStore.locale) {
        result.meta.push({
          key: `og:locale:alternate:${i18nStore.locale.name}`,
          name: 'og:locale:alternate',
          content: i18nStore.locale.name,
        });
        
        result.link.push({
          key: `alternate:${i18nStore.locale.name}`,
          rel: 'alternate',
          href: `${window.location.origin}${window.location.pathname}`,
          hreflang: i18nStore.locale.name,
        });
        
        result.link.push({
          key: `canonical:${i18nStore.locale.name}`,
          rel: 'canonical',
          href: `${window.location.origin}${window.location.pathname}`,
          hreflang: i18nStore.locale.name,
        });

        if (i18nStore.locale.name === i18nStore.locales[0].name) {
          result.link.push({
            key: `alternate:${i18nStore.locale.name}`,
            rel: 'alternate',
            href: `${window.location.origin}${window.location.pathname}`,
            hreflang: 'x-default',
          });
        }
      }

      // looop every other locales
      each(i18nStore.otherLocales, (locale) => {
        result.meta.push({
          key: `og:locale:alternate:${locale.name}`,
          name: 'og:locale:alternate',
          content: locale.name,
        });
        // if we ever receive an object instead of an array when there is only 2 locale on the website
        if (dataTranslations && dataTranslations.id === locale.id) {
          result.link.push({
            key: `alternate:${locale.name}`,
            rel: 'alternate',
            href: dataTranslations.url.indexOf(getEnv(locale.env)) > -1 ? dataTranslations.url : `${getEnv(locale.env)}${dataTranslations.url}`,
            hreflang: locale.name,
          });

          if (locale.name === i18nStore.locales[0].name) {
            result.link.push({
              key: `alternate:${locale.name}`,
              rel: 'alternate',
              href: dataTranslations.url.indexOf(getEnv(locale.env)) > -1 ? dataTranslations.url : `${getEnv(locale.env)}${dataTranslations.url}`,
              hreflang: 'x-default',
            });
          }
        }
        
        // normal behavior on multi locales website.
        if (dataTranslations) {
          const translation = find(dataTranslations, { 'siteId': locale.id });
          if (translation) {
            result.link.push({
              key: `alternate:${locale.name}`,
              rel: 'alternate',
              href: translation.url.indexOf(getEnv(locale.env)) > -1 ? translation.url : `${getEnv(locale.env)}${translation.url}`,
              hreflang: locale.name,
            });
            if (locale.name === i18nStore.locales[0].name) {
              result.link.push({
                key: `alternate:${locale.name}`,
                rel: 'alternate',
                href: translation.url.indexOf(getEnv(locale.env)) > -1 ? translation.url : `${getEnv(locale.env)}${translation.url}`,
                hreflang: 'x-default',
              });
            }
          }
        }

      });
    }

    if (dataTranslations) {
      state.value.translations = dataTranslations;
    } else {
      state.value.translations = undefined;
    }

    // handle 404
    if (appStore.is404) {
      result.meta.push({
        name: 'prerender-status-code',
        content: '404',
      });
    }

    // handle redirection
    if (isRedirectedFrom) {
      result.meta.push(
        {
          name: 'prerender-status-code',
          content: '301',
        },
        {
          name: 'prerender-header', 
          content: `Location: ${document.location.origin}${router.currentRoute.value.path}`
        }
      );
    }

    state.value.metadata = result;
  }

  return {
    state,

    // getters
    metadata: computed( () => state.value.metadata),
    translations: computed( () => state.value.translations),

    //actions
    UPDATE,
  }
});

export const store = useStore(pinia);
export default store;

/** compute SEO DATA
 * here data can be quite simple {title, description, image} OR full SeoData {title, description, socials: {facebook...}}
 */
function computeSeoData(data:Obj): SeoData{
  if (!data || typeof data !== 'object') {
    return {}
  }
  if(data.seo) return data.seo
  
  const {title, description, image} = data
  return {
    title, description,
    socials: {
      facebook: {title, description, image},
      twitter: {title, description, image},
    }
  }
}
